import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBookOpen,
  FaCommentDots,
  FaGlobeEurope,
  FaHeadset,
  FaInfoCircle,
  FaSave,
  FaTimes,
} from "react-icons/fa";

import { toast } from "react-toastify";

import {
  INITIAL_TUTORIAL_FORM,
} from "../constants/tutorialAdminOptions";

import {
  normalizeTutorialForm,
} from "../utils/tutorialAdminUtils";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-4 focus:ring-violet-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-violet-900/40";

const TABS = [
  {
    id: "general",
    label: "Général",
    icon: <FaInfoCircle />,
  },
  {
    id: "introduction",
    label: "Introduction",
    icon: <FaBookOpen />,
  },
  {
    id: "feedback",
    label: "Feedback",
    icon: <FaCommentDots />,
  },
  {
    id: "support",
    label: "Support",
    icon: <FaHeadset />,
  },
];

export default function TutorialFormDrawer({
  open,
  mode,
  tutorial,
  saving,
  onClose,
  onSave,
}) {
  const [activeTab, setActiveTab] =
    useState("general");

  const [formData, setFormData] = useState(
    INITIAL_TUTORIAL_FORM
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveTab("general");

    setFormData(
      mode === "edit"
        ? normalizeTutorialForm(tutorial)
        : INITIAL_TUTORIAL_FORM
    );

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (
        event.key === "Escape" &&
        !saving
      ) {
        onClose?.();
      }
    };

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    mode,
    tutorial,
    saving,
    onClose,
  ]);

  const summary = useMemo(
    () => ({
      scope: formData.site_key
        ? `Site : ${formData.site_key}`
        : "Tous les sites",

      version: `Version ${formData.version || 1}`,

      application:
        formData.app_key ||
        "Application non renseignée",
    }),
    [
      formData.site_key,
      formData.version,
      formData.app_key,
    ]
  );

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((current) => ({
      ...current,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (!formData.app_key.trim()) {
      toast.error(
        "La clé de l’application est obligatoire."
      );

      setActiveTab("general");
      return;
    }

    if (!formData.titre.trim()) {
      toast.error(
        "Le titre du tutoriel est obligatoire."
      );

      setActiveTab("general");
      return;
    }

    const version = Number(
      formData.version
    );

    if (
      !Number.isInteger(version) ||
      version < 1
    ) {
      toast.error(
        "La version doit être supérieure ou égale à 1."
      );

      setActiveTab("general");
      return;
    }

    await onSave?.({
      ...formData,
      site_key:
        formData.site_key.trim() || null,
      version,
    });
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1300]">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={
          saving ? undefined : onClose
        }
        aria-label="Fermer"
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-5xl flex-col border-l border-slate-200 bg-white shadow-[-30px_0_90px_-35px_rgba(0,0,0,0.55)] dark:border-slate-800 dark:bg-slate-900">
        <header className="relative overflow-hidden border-b border-white/10 bg-slate-950 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.35),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(217,70,239,0.25),transparent_30%)]" />

          <div className="relative p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/75">
                  <FaBookOpen />

                  {mode === "edit"
                    ? "Modification"
                    : "Nouveau tutoriel"}
                </div>

                <h2 className="mt-3 text-2xl font-semibold">
                  {mode === "edit"
                    ? "Modifier le tutoriel"
                    : "Créer un tutoriel"}
                </h2>

                <p className="mt-1 max-w-2xl text-sm text-white/70">
                  Configure les informations générales.
                  Le contenu détaillé sera géré dans
                  l’éditeur dédié.
                </p>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={onClose}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10 transition hover:bg-white/20 disabled:opacity-50"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() =>
                    setActiveTab(tab.id)
                  }
                  className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-medium transition ${
                    activeTab === tab.id
                      ? "bg-white text-slate-900"
                      : "border border-white/15 bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex-1 overflow-y-auto bg-slate-50 p-4 dark:bg-slate-950/50 sm:p-6">
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
              <div className="xl:col-span-8">
                <section className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
                  {activeTab === "general" && (
                    <GeneralFields
                      formData={formData}
                      mode={mode}
                      onChange={handleChange}
                    />
                  )}

                  {activeTab ===
                    "introduction" && (
                    <IntroductionFields
                      formData={formData}
                      onChange={handleChange}
                    />
                  )}

                  {activeTab === "feedback" && (
                    <FeedbackFields
                      formData={formData}
                      onChange={handleChange}
                    />
                  )}

                  {activeTab === "support" && (
                    <SupportFields
                      formData={formData}
                      onChange={handleChange}
                    />
                  )}
                </section>
              </div>

              <div className="xl:col-span-4">
                <div className="sticky top-0 space-y-4">
                  <section className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <SectionTitle
                      title="Résumé"
                      description="Portée de cette version."
                    />

                    <div className="mt-5 space-y-3">
                      <SummaryRow
                        icon={<FaBookOpen />}
                        label="Application"
                        value={summary.application}
                      />

                      <SummaryRow
                        icon={<FaGlobeEurope />}
                        label="Portée"
                        value={summary.scope}
                      />

                      <SummaryRow
                        icon={<FaInfoCircle />}
                        label="Version"
                        value={summary.version}
                      />
                    </div>
                  </section>

                  <label className="flex cursor-pointer items-center justify-between gap-4 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        Tutoriel actif
                      </div>

                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Permet son utilisation après
                        publication.
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      name="is_active"
                      checked={
                        formData.is_active
                      }
                      onChange={handleChange}
                      className="h-5 w-5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <footer className="border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={saving}
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaSave />

                {saving
                  ? "Enregistrement..."
                  : mode === "edit"
                  ? "Enregistrer"
                  : "Créer le tutoriel"}
              </button>
            </div>
          </footer>
        </form>
      </aside>
    </div>
  );
}

function GeneralFields({
  formData,
  mode,
  onChange,
}) {
  return (
    <>
      <SectionTitle
        title="Informations générales"
        description="Application, portée, langue et version."
      />

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field>
          <Label required>
            Clé de l’application
          </Label>

          <input
            className={INPUT}
            name="app_key"
            value={formData.app_key}
            onChange={onChange}
            placeholder="monappli-club"
          />
        </Field>

        <Field>
          <Label>Site client</Label>

          <input
            className={INPUT}
            name="site_key"
            value={formData.site_key}
            onChange={onChange}
            placeholder="Vide = tous les sites"
          />
        </Field>

        <Field>
          <Label required>Langue</Label>

          <input
            className={INPUT}
            name="locale"
            value={formData.locale}
            onChange={onChange}
            placeholder="fr-FR"
          />
        </Field>

        <Field>
          <Label required>Version</Label>

          <input
            className={`${INPUT} ${
              mode === "edit"
                ? "cursor-not-allowed opacity-60"
                : ""
            }`}
            type="number"
            min="1"
            name="version"
            value={formData.version}
            onChange={onChange}
            disabled={mode === "edit"}
          />
        </Field>

        <Field className="md:col-span-2">
          <Label required>
            Titre du tutoriel
          </Label>

          <input
            className={INPUT}
            name="titre"
            value={formData.titre}
            onChange={onChange}
            placeholder="Tutoriel • MonAppliClub"
          />
        </Field>

        <Field className="md:col-span-2">
          <Label>Sous-titre</Label>

          <textarea
            className={`${INPUT} min-h-[110px] resize-y leading-6`}
            name="sous_titre"
            value={formData.sous_titre}
            onChange={onChange}
          />
        </Field>
      </div>
    </>
  );
}

function IntroductionFields({
  formData,
  onChange,
}) {
  return (
    <>
      <SectionTitle
        title="Introduction"
        description="Présentation affichée au début du tutoriel."
      />

      <div className="mt-6 space-y-4">
        <Field>
          <Label>Libellé du sommaire</Label>

          <input
            className={INPUT}
            name="intro_label"
            value={formData.intro_label}
            onChange={onChange}
          />
        </Field>

        <Field>
          <Label>Titre d’introduction</Label>

          <input
            className={INPUT}
            name="intro_titre"
            value={formData.intro_titre}
            onChange={onChange}
          />
        </Field>

        <Field>
          <Label>Contenu</Label>

          <textarea
            className={`${INPUT} min-h-[260px] resize-y leading-7`}
            name="intro_contenu"
            value={formData.intro_contenu}
            onChange={onChange}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <Label>Libellé FAQ</Label>

            <input
              className={INPUT}
              name="faq_label"
              value={formData.faq_label}
              onChange={onChange}
            />
          </Field>

          <Field>
            <Label>Titre FAQ</Label>

            <input
              className={INPUT}
              name="faq_titre"
              value={formData.faq_titre}
              onChange={onChange}
            />
          </Field>
        </div>
      </div>
    </>
  );
}

function FeedbackFields({
  formData,
  onChange,
}) {
  return (
    <>
      <SectionTitle
        title="Satisfaction utilisateur"
        description="Question affichée à la fin du tutoriel."
      />

      <div className="mt-6 space-y-4">
        <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-50">
              Afficher la question
            </div>

            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Active la zone de satisfaction.
            </div>
          </div>

          <input
            type="checkbox"
            name="afficher_feedback"
            checked={
              formData.afficher_feedback
            }
            onChange={onChange}
            className="h-5 w-5 rounded border-slate-300 text-violet-600"
          />
        </label>

        <Field>
          <Label>Question</Label>

          <input
            className={INPUT}
            name="feedback_question"
            value={formData.feedback_question}
            onChange={onChange}
          />
        </Field>

        <Field>
          <Label>Description</Label>

          <textarea
            className={`${INPUT} min-h-[150px] resize-y leading-6`}
            name="feedback_description"
            value={
              formData.feedback_description
            }
            onChange={onChange}
          />
        </Field>
      </div>
    </>
  );
}

function SupportFields({
  formData,
  onChange,
}) {
  return (
    <>
      <SectionTitle
        title="Informations de support"
        description="Coordonnées affichées à la fin du tutoriel."
      />

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field className="md:col-span-2">
          <Label>Titre</Label>

          <input
            className={INPUT}
            name="support_titre"
            value={formData.support_titre}
            onChange={onChange}
          />
        </Field>

        <Field className="md:col-span-2">
          <Label>Description</Label>

          <textarea
            className={`${INPUT} min-h-[120px] resize-y leading-6`}
            name="support_description"
            value={
              formData.support_description
            }
            onChange={onChange}
          />
        </Field>

        <Field>
          <Label>Nom</Label>

          <input
            className={INPUT}
            name="support_nom"
            value={formData.support_nom}
            onChange={onChange}
          />
        </Field>

        <Field>
          <Label>Email</Label>

          <input
            type="email"
            className={INPUT}
            name="support_email"
            value={formData.support_email}
            onChange={onChange}
          />
        </Field>

        <Field>
          <Label>Téléphone</Label>

          <input
            className={INPUT}
            name="support_telephone"
            value={
              formData.support_telephone
            }
            onChange={onChange}
          />
        </Field>
      </div>
    </>
  );
}

function SectionTitle({
  title,
  description,
}) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        {title}
      </h3>

      {description ? (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {description}
        </p>
      ) : null}
    </div>
  );
}

function Field({
  children,
  className = "",
}) {
  return (
    <div className={className}>
      {children}
    </div>
  );
}

function Label({
  children,
  required = false,
}) {
  return (
    <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
      {children}

      {required ? (
        <span className="ml-1 text-rose-500">
          *
        </span>
      ) : null}
    </label>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-800/50">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-violet-600 shadow-sm dark:bg-slate-900 dark:text-violet-300">
        {icon}
      </div>

      <div className="min-w-0">
        <div className="text-xs uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
          {label}
        </div>

        <div className="mt-1 break-words text-sm font-semibold text-slate-900 dark:text-slate-50">
          {value || "—"}
        </div>
      </div>
    </div>
  );
}